import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { ResponseData } from '@freelancer/datastore';
import { TotpMethod } from '@freelancer/datastore/collections';
import { Facebook, FacebookUser } from '@freelancer/facebook';
import {
  AuthResponse,
  AuthSuccessResponseAjax,
  LoginError,
  LoginSignupService,
  TwoFactorResponse,
} from '@freelancer/login-signup';
import { ThreatmetrixService } from '@freelancer/threatmetrix';
import { ContainerSize } from '@freelancer/ui/container';
import { BackgroundColor } from '@freelancer/ui/page-layout';
import { required } from '@freelancer/ui/validators';
import { ErrorCodeApi } from 'api-typings/errors/errors';
import { SuccessAction } from '../success-redirect/success-redirect.component';

export enum LoginStep {
  CREDENTIALS = 0,
  TWO_FACTOR_AUTH = 1,
  SUCCESS = 2,
  RESET_PASSWORD = 3,
  FACEBOOK_LINK = 4,
}

@Component({
  selector: 'app-login',
  template: `
    <fl-card [ngSwitch]="currentStep">
      <app-credentials-form
        *ngSwitchCase="LoginStep.CREDENTIALS"
        [response]="loginPromise && (loginPromise | async)"
        [facebookSdkLoaded]="
          facebookSdkPromise && (facebookSdkPromise | async) === true
        "
        [formGroup]="formGroup"
        (complete)="handleLogin()"
        (resetPassword)="handleResetPassword()"
        (facebookClick)="handleFacebookLogin()"
        (switchToSignup)="handleSwitchToSignup()"
      ></app-credentials-form>
      <app-two-factor-form
        *ngSwitchCase="LoginStep.TWO_FACTOR_AUTH"
        [method]="twoFactorMethodPromise | async"
        [resendResponse]="resendPromise && (resendPromise | async)"
        [response]="twoFactorPromise && (twoFactorPromise | async)"
        [formGroup]="twoFactorFormGroup"
        (back)="handleBack()"
        (complete)="handleVerify()"
        (resend)="handleResend()"
      ></app-two-factor-form>
      <app-reset-password
        *ngSwitchCase="LoginStep.RESET_PASSWORD"
        [domain]="'Freelancer.com'"
        [email]="formGroup.get('user')?.value"
        [resetService]="loginSignup"
        (back)="handleBack()"
      ></app-reset-password>
      <app-facebook-link
        *ngSwitchCase="LoginStep.FACEBOOK_LINK"
        [facebookUser]="facebookDetailsPromise | async"
        [response]="facebookLinkPromise && (facebookLinkPromise | async)"
        [formGroup]="formGroup"
        (back)="handleBack()"
        (complete)="handleFacebookLink()"
        (resetPassword)="handleResetPassword()"
      ></app-facebook-link>
      <app-success-redirect
        *ngSwitchCase="LoginStep.SUCCESS"
        [action]="SuccessAction.LOGIN"
      ></app-success-redirect>
    </fl-card>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit {
  BackgroundColor = BackgroundColor;
  ContainerSize = ContainerSize;

  LoginStep = LoginStep;
  SuccessAction = SuccessAction;

  currentStep = LoginStep.CREDENTIALS;
  formGroup = this.fb.group(
    {
      user: ['', required('Please enter your username or email')],
      password: ['', required('Please enter your password')],
      rememberMe: [false],
    },
    {
      updateOn: 'submit',
    },
  );
  twoFactorFormGroup = this.fb.group({
    totp: '',
  });

  twoFactorMethodPromise: Promise<TotpMethod | undefined>;

  loginPromise: Promise<ResponseData<AuthResponse, LoginError>>;
  resendPromise: Promise<ResponseData<TwoFactorResponse, LoginError>>;
  twoFactorPromise: Promise<ResponseData<AuthSuccessResponseAjax, LoginError>>;

  /**
   * Facebook Auth promise via the Facebook SDK.
   * Returns the facebook auth details
   */
  facebookAuthPromise: Promise<ResponseData<fb.AuthResponse, fb.LoginStatus>>;
  facebookDetailsPromise: Promise<FacebookUser | undefined>;
  facebookLinkPromise: Promise<
    ResponseData<AuthSuccessResponseAjax, LoginError>
  >;
  facebookSdkPromise: Promise<boolean>;

  @Input() isModal = false;

  @Output() success = new EventEmitter<number>();
  @Output() switchToSignup = new EventEmitter();

  constructor(
    public loginSignup: LoginSignupService,
    private facebook: Facebook,
    private fb: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router,
    private threatmetrix: ThreatmetrixService,
  ) {}

  ngOnInit() {
    this.facebookSdkPromise = this.facebook.loadSdk();

    const savedState = this.loginSignup.getSavedState();
    if ('user' in savedState) {
      this.formGroup.controls.user.setValue(savedState.user);
      this.formGroup.controls.password.setValue(savedState.password);
    } else {
      this.facebookAuthPromise = savedState.facebookAuthPromise;
      this.facebookDetailsPromise = savedState.facebookDetailsPromise;
      this.setStep(LoginStep.FACEBOOK_LINK);
    }

    this.threatmetrix.load();
  }

  setStep(step: LoginStep) {
    this.currentStep = step;
    this.changeDetectorRef.markForCheck();
  }

  handleLogin() {
    this.loginPromise = this.loginSignup
      .login(this.formGroup.value.user, this.formGroup.value.password)
      .then(async response => {
        // if the user is suspended, we redirect them to a special page
        // suspended users can't log in so we can't do this as part of handleSuccess
        if (
          response.status === 'error' &&
          response.errorCode === ErrorCodeApi.AUTH_ACCOUNT_SUSPENDED
        ) {
          // maintain busy state until the navigation goes through
          await this.router.navigate(['users/usersuspended.php']);
          return response;
        }
        return response;
      });

    this.twoFactorMethodPromise = this.loginPromise.then(response => {
      if (
        // failed login; no 2FA message
        response.status !== 'success' ||
        // successful login; no 2FA message
        !('preLoginToken' in response.result)
      ) {
        return;
      }
      return response.result.method;
    });
    this.loginPromise.then(response => {
      if (response.status === 'success') {
        if ('preLoginToken' in response.result) {
          this.setStep(LoginStep.TWO_FACTOR_AUTH);
        } else {
          this.handleSuccess(response.result.user, response.result.token);
        }
      }
    });
  }

  handleVerify() {
    this.twoFactorPromise = this.loginPromise.then(response => {
      if (
        response.status !== 'success' ||
        !('preLoginToken' in response.result)
      ) {
        // this shouldn't happen because we shouldn't be able to call `handleVerify`
        // when the loginPromise hasn't returned success
        return {
          status: 'error',
          errorCode: 'UNKNOWN_ERROR',
        };
      }

      return this.loginSignup.verify(
        response.result.preLoginToken,
        this.twoFactorFormGroup.value.totp,
      );
    });

    this.twoFactorPromise.then(response => {
      if (response.status === 'success') {
        this.handleSuccess(response.result.user, response.result.token);
      }
    });
  }

  handleResend() {
    this.resendPromise = this.loginPromise.then(response => {
      if (
        response.status !== 'success' ||
        !('preLoginToken' in response.result)
      ) {
        // this shouldn't happen because we shouldn't be able to call `handleVerify`
        // when the loginPromise hasn't returned success
        return {
          status: 'error',
          errorCode: 'UNKNOWN_ERROR',
        } as const;
      }

      return this.loginSignup.resend2FACode(
        response.result.preLoginToken,
        response.result.method,
      );
    });
    // update preLoginToken in loginPromise.
    this.loginPromise = this.resendPromise;
  }

  handleSuccess(userId: number, authToken: string) {
    this.setStep(LoginStep.SUCCESS);

    this.loginSignup
      .handleSuccess(
        'login',
        userId,
        authToken,
        this.formGroup.value.rememberMe,
        {
          redirectDisabled: this.isModal,
        },
      )
      .then(() => this.success.emit(userId));
  }

  handleBack() {
    // always go back to credentials form
    this.setStep(LoginStep.CREDENTIALS);
  }

  handleResetPassword() {
    this.setStep(LoginStep.RESET_PASSWORD);
  }

  handleFacebookLogin() {
    this.facebookAuthPromise = this.facebook.login();
    this.facebookDetailsPromise = this.facebookAuthPromise.then(response => {
      if (response.status === 'error') {
        return undefined;
      }
      return this.facebook.getUserDetails(response.result);
    });

    // store this in a local var because it has a more restrictive type than loginPromise
    const facebookLoginPromise = this.facebookAuthPromise.then(response => {
      if (response.status === 'error') {
        return {
          status: 'error',
          errorCode: ErrorCodeApi.AUTH_FACEBOOK_LINK_FAILED,
        } as const;
      }
      return this.loginSignup.facebookLogin(response.result);
    });

    this.loginPromise = facebookLoginPromise.then(async loginResponse => {
      if (loginResponse.status === 'success') {
        this.handleSuccess(
          loginResponse.result.user,
          loginResponse.result.token,
        );
      } else if (
        loginResponse.errorCode === ErrorCodeApi.AUTH_USER_MISSING ||
        loginResponse.errorCode === ErrorCodeApi.FACEBOOK_EMAIL_NOT_FOUND
      ) {
        this.handleFacebookSwitchToSignup();
      } else if (
        loginResponse.errorCode === ErrorCodeApi.AUTH_FACEBOOK_EMAIL_EXISTS
      ) {
        // keeping spinning until the next step is fully ready to render
        const details = await this.facebookDetailsPromise;
        if (!details) {
          return {
            status: 'error',
            errorCode: ErrorCodeApi.AUTH_FACEBOOK_LINK_FAILED,
          } as const;
        }
        this.setStep(LoginStep.FACEBOOK_LINK);
      }
      return loginResponse;
    });
  }

  handleFacebookLink() {
    this.facebookLinkPromise = this.facebookAuthPromise.then(response => {
      if (response.status !== 'success') {
        // this shouldn't happen because we shouldn't be able to call `handleFacebookLoginLink`
        // when the facebookAuthPromise hasn't returned success
        return {
          status: 'error',
          errorCode: 'UNKNOWN_ERROR',
        };
      }

      return this.loginSignup.facebookLinkLogin(
        this.formGroup.value.user,
        this.formGroup.value.password,
        response.result,
      );
    });
    this.facebookLinkPromise.then(response => {
      if (response.status === 'success') {
        this.handleSuccess(response.result.user, response.result.token);
      }
    });
  }

  handleSwitchToSignup() {
    this.loginSignup.saveState({
      user: this.formGroup.value.user,
      password: this.formGroup.value.password,
    });

    this.switchToSignup.emit();
  }

  handleFacebookSwitchToSignup() {
    // save facebook state and redirect to login to access link components
    this.loginSignup.saveState({
      facebookAuthPromise: this.facebookAuthPromise,
      facebookDetailsPromise: this.facebookDetailsPromise,
    });

    this.switchToSignup.emit();
  }
}
